import { UserAvatar } from "@/components/UserAvatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface ReadReceiptIndicatorProps {
  readReceipts: Array<{
    messageId: string;
    userId: string;
    readAt: string;
  }>;
  getUserDisplayName?: (userId: string) => string;
  isCurrentUser: boolean;
}

export default function ReadReceiptIndicator({ 
  readReceipts, 
  getUserDisplayName,
  isCurrentUser 
}: ReadReceiptIndicatorProps) {
  if (!isCurrentUser || readReceipts.length === 0) {
    return null;
  }

  const getInitials = (userId: string) => {
    if (getUserDisplayName) {
      const name = getUserDisplayName(userId);
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    }
    return 'U';
  };

  const getTooltipText = () => {
    if (readReceipts.length === 1) {
      const receipt = readReceipts[0];
      const name = getUserDisplayName ? getUserDisplayName(receipt.userId) : 'Someone';
      const time = new Date(receipt.readAt).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
      return `Read by ${name} at ${time}`;
    } else {
      const names = readReceipts
        .slice(0, 3)
        .map(r => getUserDisplayName ? getUserDisplayName(r.userId) : 'Someone');
      const remaining = readReceipts.length - 3;
      
      if (remaining > 0) {
        return `Read by ${names.join(', ')} and ${remaining} other${remaining > 1 ? 's' : ''}`;
      } else {
        return `Read by ${names.join(', ')}`;
      }
    }
  };

  return (
    <div className="flex items-center space-x-1 mt-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex -space-x-1">
            {readReceipts.slice(0, 3).map((receipt, index) => (
              <UserAvatar
                key={receipt.userId}
                userId={receipt.userId}
                fallback={getInitials(receipt.userId)}
                size="sm"
                className="h-4 w-4 border border-white"
                showLevel={false}
              />
            ))}
            {readReceipts.length > 3 && (
              <div className="h-4 w-4 bg-gray-300 rounded-full border border-white flex items-center justify-center">
                <span className="text-xs text-gray-600 font-bold">
                  +{readReceipts.length - 3}
                </span>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{getTooltipText()}</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}